import { paths } from '@luxuryescapes/contract-svc-merchandise'
import { without } from 'lib/array/arrayUtils'

type Response = paths['/api/merchandise/adverts/{id}']['get']['responses']['200']['content']['application/json']
type ServerMerchandise = NonNullable<Response['result']['advert']>

const colourMap = new Map<string, string>([
  ['Beige', '#FFEFD4'],
  ['Black', '#131313'],
  ['Blue', '#8BC0FF'],
  ['Bronze', '#AD7E5F'],
  ['Brown', '#352319'],
  ['Clear', '#F2E9E9'],
  ['Gold', '#AEA478'],
  ['Gray', '#505460'],
  ['Green', '#40BC5B'],
  ['Multicolor', ''],
  ['Navy', '#1F3579'],
  ['Orange', '#E9AE6C '],
  ['Pink', '#F97FB4'],
  ['Purple', '#8685E0'],
  ['Red', '#D43044 '],
  ['Rose gold', '#F48189'],
  ['Silver', '#BABCC2'],
  ['White', '#FFFFFF'],
  ['Yellow', '#F48189'],
])

function mapMerchandiseVariant(
  variant: ServerMerchandise['variants'][number],
  images: Array<App.Image>,
  serverMerchandise: ServerMerchandise,
): App.MerchandiseOfferVariant {
  let variantImages = images
  if (variant.images.length > 0) {
    variantImages = variant.images.map((image): App.Image => ({
      id: image.imageId,
      title: image.alt ?? undefined,
    }))
  }
  const price = variant.price ?? serverMerchandise.price ?? 0

  const mappedOptions = variant.variantOptionValues.filter(option => option.optionValue).map((option): App.MerchandiseOfferOption => {
    return {
      id: option.id,
      type: option.optionType.displayName,
      name: option.optionValue!.displayName,
    }
  })
  const sizeOption = mappedOptions.find(option => option.type === 'Size')
  const colourOption = mappedOptions.find(option => option.type === 'Color')

  return {
    id: variant.id,
    sku: variant.sku ?? undefined,
    label: variant.label,
    maxPurchaseQuantity: variant.maxPurchaseQuantity ?? undefined,
    minPurchaseQuantity: variant.minPurchaseQuantity ?? undefined,
    inventory: variant.countOnHand ?? 0,
    price,
    pointsPrice: price * 5,
    options: without(mappedOptions, sizeOption, colourOption),
    images: variantImages,
    colour: colourOption ? {
      ...colourOption,
      colour: colourMap.get(colourOption.name) ?? '',
    } : undefined,
    size: sizeOption,
  }
}

function mapMerchandiseOffer(serverMerchandise: ServerMerchandise): App.MerchandiseOffer {
  const images: Array<App.Image> = []

  if (serverMerchandise.imageId) {
    images.push({ id: serverMerchandise.imageId })
  }

  if (serverMerchandise.images.length > 0) {
    images.push(...serverMerchandise.images.map((image): App.Image => ({
      id: image.imageId,
      title: image.alt ?? undefined,
    })))
  }
  const variants = serverMerchandise.variants.map((variant) => mapMerchandiseVariant(variant, images, serverMerchandise))

  return {
    type: 'merchandise',
    parentType: 'merchandise',
    analyticsType: 'merchandise',
    productType: 'merchandise',
    loyaltyProductType: (serverMerchandise.brand as any).loyalty ?? 'shop_tier_1',
    id: serverMerchandise.id,
    slug: serverMerchandise.name,
    seller: {
      id: serverMerchandise.seller.id,
      name: serverMerchandise.seller.businessName,
      emailAddress: serverMerchandise.seller.emailAddress,
    },
    luxPlus: {},
    name: serverMerchandise.name,
    category: serverMerchandise.category ? {
      id: serverMerchandise.category.id,
      slug: serverMerchandise.category.slug,
      name: serverMerchandise.category.displayName,
      parentId: serverMerchandise.category.parentId ?? undefined,
    } : undefined,
    brand: {
      id: serverMerchandise.brand!.id,
      name: serverMerchandise.brand!.name,
    },
    images,
    leadVariant: variants[0],
    variants,
    copy: {
      description: serverMerchandise.description ?? undefined,
      features: serverMerchandise.productFeatures ?? undefined,
      specifications: serverMerchandise.specifications ?? undefined,
      shippingPolicy: serverMerchandise.seller.shippingPolicy ?? undefined,
      returnPolicy: serverMerchandise.seller.returnPolicy ?? undefined,
    },
  }
}

export default mapMerchandiseOffer
